import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import wordmarkLight from '../assets/da2026/abrf_wordmark_light.png';
import bulldozer from '../assets/da2026/bulldozer.png';
import cone from '../assets/da2026/cone.png';
import teleradLogo from '../assets/da2026/telerad_light.png';
import erleLogo from '../assets/da2026/erle_vertical_white.svg';

const CARO_BANDS = [
  'from-[#20364B] to-[#141312]',
  'from-[#F26522] to-[#D9531A]',
  'from-[#F59B3B] to-[#D9531A]',
];

function Home() {
  const [festivalState, setFestivalState] = useState('avant');
  const [tournaments, setTournaments] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.from('match').select('score_equipe1,score_equipe2,equipe1_texte');
        if (!error && data) {
          const valid = data.filter(m => m.equipe1_texte && String(m.equipe1_texte).trim() !== '');
          const hasScore = valid.some(m => m.score_equipe1 != null && m.score_equipe2 != null);
          const hasPending = valid.some(m => m.score_equipe1 == null || m.score_equipe2 == null);
          if (hasScore && hasPending) setFestivalState('en_cours');
          else if (hasScore && !hasPending) setFestivalState('termine');
          else setFestivalState('avant');
        }
      } catch (e) {}

      try {
        let res = await supabase.from('tournoi').select('*').order('ordre');
        if (res.error) res = await supabase.from('tournois').select('*').order('ordre');
        if (!res.error && res.data) {
          setTournaments(res.data.map(t => ({
            id: t.id,
            name: t.nom || t.name || `Tournoi ${t.id}`,
            description: t.description || '',
          })));
        }
      } catch (e) {}
    })();
  }, []);

  const cta = {
    avant: { label: 'Voir les tournois', icon: 'ri-trophy-fill' },
    en_cours: { label: 'Voir les matchs en direct', icon: 'ri-flashlight-fill' },
    termine: { label: 'Voir les classements', icon: 'ri-trophy-fill' },
  }[festivalState];

  return (
    <div className="max-w-md mx-auto pb-20">
      {/* ===== HERO ===== */}
      <section className="relative bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white overflow-hidden rounded-b-3xl shadow-hero px-5 pt-8 pb-7">
        <div className="absolute top-0 inset-x-0 h-2 hazard-white"></div>

        {/* Partenaire officiel (Telerad) & concepteur de l'appli (Erle) */}
        <div className="flex items-center justify-between">
          <a
            href="https://www.telerad.fr/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Telerad, partenaire officiel"
            className="inline-flex items-center bg-white/15 border border-white/25 px-4 py-2.5 rounded-full active:scale-[.97] transition-transform"
          >
            <img src={teleradLogo} alt="Telerad" className="h-[19px] w-auto" />
          </a>
          <a
            href="https://erlesolutions.fr"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Application développée par Erle Solutions"
            className="w-11 h-11 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center active:scale-[.97] transition-transform"
          >
            <img src={erleLogo} alt="Erle Solutions" className="h-6 w-auto" />
          </a>
        </div>

        <img src={wordmarkLight} alt="Anglet Beach Rugby Festival" className="block w-52 max-w-[70%] mx-auto mt-5 mb-1.5" />
        <p className="text-center text-[13px] text-white/90 max-w-[17rem] mx-auto">Suivez tous les résultats et classements en direct</p>

        <img src={bulldozer} alt="" aria-hidden="true" className="block w-72 max-w-[92%] mx-auto -mb-1 drop-shadow-[0_18px_22px_rgba(20,19,18,0.25)]" />

        <div className="flex gap-3 items-stretch relative z-10">
          {/* Carte état */}
          <div className="flex-1 bg-white/15 border border-white/25 rounded-2xl px-4 py-3.5 flex flex-col justify-center">
            {festivalState === 'en_cours' ? (
              <span className="flex items-center gap-2 font-display text-2xl leading-none">
                <span className="w-2.5 h-2.5 rounded-full bg-white live-dot"></span> Live
              </span>
            ) : festivalState === 'termine' ? (
              <span className="font-display text-2xl leading-none">Fini</span>
            ) : (
              <span className="font-display text-2xl leading-none">Bientôt</span>
            )}
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] mt-1.5 text-white/85">
              {festivalState === 'en_cours' ? "Le festival est\nlancé" : festivalState === 'termine' ? 'Édition\nterminée' : "Avant le\ncoup d'envoi"}
            </span>
          </div>

          {/* CTA */}
          <Link to="/tournaments" className="flex-[1.15] bg-white text-brand-ink rounded-2xl px-4 py-3.5 flex flex-col justify-center gap-1.5 shadow-card active:scale-[.98] transition-transform">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-brand-muted">C'est parti</span>
            <span className="font-display text-[15px] flex items-center gap-2">
              <i className={`${cta.icon} text-brand-orange text-lg`}></i>
              {cta.label}
            </span>
          </Link>
        </div>
      </section>

      {/* ===== À LA UNE ===== */}
      <section className="px-5 pt-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-orange-deep">À la une</div>
        <h2 className="h-display text-xl text-brand-ink mt-1.5 mb-3">L'actu du festival</h2>
        <article className="card overflow-hidden">
          <div className="relative h-32 bg-gradient-to-br from-brand-orange to-brand-orange-deep flex items-center justify-center overflow-hidden">
            <img src={cone} alt="" className="w-24 drop-shadow-[0_10px_14px_rgba(20,19,18,0.28)]" />
            <span className="absolute top-3.5 left-3.5 bg-white text-brand-orange-deep text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">Nouveauté 2026</span>
            <div className="absolute bottom-0 inset-x-0 h-1.5 hazard-white"></div>
          </div>
          <div className="p-5">
            <h3 className="h-display text-xl text-brand-ink mb-2">Nouveauté 2026</h3>
            <p className="text-sm text-brand-muted leading-relaxed">L'Anglet Beach Rugby Festival lance sa soirée opening !</p>
            <div className="flex items-center gap-2 mt-3 text-sm font-semibold text-brand-orange-deep">
              <i className="ri-calendar-event-fill"></i> Rendez-vous jeudi 30 juillet à partir de 18h
            </div>
          </div>
        </article>
      </section>

      {/* ===== CARROUSEL TOURNOIS ===== */}
      {tournaments.length > 0 && (
        <section className="pt-7">
          <div className="px-5 flex items-end justify-between mb-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-orange-deep">Programme</div>
              <h2 className="h-display text-xl text-brand-ink mt-1.5">Les tournois</h2>
            </div>
            <Link to="/tournaments" className="text-xs font-bold text-brand-orange flex items-center gap-0.5">
              Tout voir <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="flex gap-3.5 overflow-x-auto no-sb px-5 pb-1 snap-x">
            {tournaments.map((t, i) => {
              const parts = (t.description || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
              const day = parts.length ? parts[parts.length - 1] : '';
              return (
                <Link
                  key={t.id}
                  to={`/tournament/${t.id}`}
                  className="shrink-0 w-52 snap-start card overflow-hidden active:scale-[.99] transition-transform"
                >
                  <div className={`relative h-24 p-3 flex items-end bg-gradient-to-br ${CARO_BANDS[i % CARO_BANDS.length]} overflow-hidden`}>
                    {day && <span className="bg-white/90 text-brand-orange-deep text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full">{day}</span>}
                  </div>
                  <div className="p-3.5">
                    <h4 className="h-display text-[15px] text-brand-ink leading-none mb-1.5">{t.name}</h4>
                    <div className="text-[11px] font-bold text-brand-orange flex items-center gap-1">Découvrir <i className="ri-arrow-right-s-line"></i></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ===== PARTENAIRES ===== */}
      <section className="px-5 pt-7">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-orange-deep">Ils font le festival</div>
        <h2 className="h-display text-xl text-brand-ink mt-1.5 mb-3">Nos partenaires</h2>

        {/* Telerad */}
        <article className="card overflow-hidden mb-4">
          <div className="relative bg-brand-ink px-5 pt-7 pb-6 flex flex-col items-center gap-3.5 overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1.5 hazard"></div>
            <span className="bg-brand-orange text-white text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Partenaire officiel</span>
            <img src={teleradLogo} alt="Telerad" className="h-9 w-auto" />
          </div>
          <div className="p-5">
            <p className="text-sm font-semibold text-brand-ink leading-snug mb-3">
              Concepteur de systèmes de radiocommunication aéronautique et maritime.
            </p>
            <p className="text-sm text-brand-muted leading-relaxed mb-3">
              Maîtrise et expertise des radiocommunications pour applications critiques.
            </p>
            <p className="text-sm text-brand-muted leading-relaxed">
              Unique concepteur et fournisseur français, nous avons pour vocation d'apporter à nos clients, civils et
              militaires, des solutions complètes de systèmes de radiocommunication aéronautique et maritime. Nos produits
              et systèmes répondent aux exigences les plus strictes. Mondialement reconnues, nos solutions innovantes ont
              pour vocation de garantir à nos clients qualité, fiabilité et performance.
            </p>
            <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-brand-line">
              <a
                href="https://www.telerad.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold text-brand-orange flex items-center gap-1.5"
              >
                www.telerad.fr <i className="ri-external-link-line"></i>
              </a>
              <span className="chip chip-soon shrink-0">
                <i className="ri-map-pin-fill"></i> Anglet
              </span>
            </div>
          </div>
        </article>

        {/* Erle Solutions */}
        <article className="card overflow-hidden">
          <div className="relative bg-brand-ink px-5 py-7 flex items-center justify-center gap-3 overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1.5 hazard"></div>
            <img src={erleLogo} alt="Erle Solutions" className="h-14 w-auto" />
          </div>
          <div className="p-5">
            <p className="text-sm font-semibold text-brand-ink leading-snug mb-3">Innovation technologique</p>
            <p className="text-sm text-brand-muted leading-relaxed">
              L'Anglet Beach Rugby Festival a choisi de faire confiance à{' '}
              <a href="https://erlesolutions.fr" target="_blank" rel="noopener noreferrer" className="font-bold text-brand-orange-deep">Erle Solutions</a>{' '}
              pour développer cette application de suivi des résultats en direct. Erle est une entreprise locale
              spécialisée dans l'intégration de solutions digitales et d'IA en entreprise. Vous pouvez les retrouver{' '}
              <a href="https://erlesolutions.fr" target="_blank" rel="noopener noreferrer" className="font-bold text-brand-orange-deep">sur leur site</a>.
            </p>
            <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-brand-line">
              <a
                href="https://erlesolutions.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold text-brand-orange flex items-center gap-1.5"
              >
                erlesolutions.fr <i className="ri-external-link-line"></i>
              </a>
              <span className="chip chip-soon shrink-0">
                <i className="ri-map-pin-fill"></i> Anglet
              </span>
            </div>
          </div>
        </article>
      </section>

      {/* ===== VISION PAR TERRAIN ===== */}
      <section className="px-5 pt-7">
        <Link
          to="/terrains"
          className="card flex items-center gap-3.5 px-4 py-4 active:scale-[.99] transition-transform"
        >
          <span className="w-11 h-11 shrink-0 rounded-2xl bg-brand-orange text-white flex items-center justify-center text-xl">
            <i className="ri-layout-grid-fill"></i>
          </span>
          <span className="min-w-0 flex-1">
            <span className="h-display text-[15px] text-brand-ink block leading-tight">Vision par terrain</span>
            <span className="text-[11px] text-brand-muted block mt-0.5">
              Tous les matchs d'un terrain, dans l'ordre
            </span>
          </span>
          <i className="ri-arrow-right-s-line text-xl text-brand-orange shrink-0"></i>
        </Link>
      </section>

      {/* ===== FOOTER ===== */}
      <section className="px-5 pt-7">
        <div className="relative bg-brand-ink text-white rounded-3xl overflow-hidden px-6 py-6 text-center">
          <div className="absolute top-0 inset-x-0 h-1.5 hazard"></div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/55 mt-1 mb-3">Powered by</div>
          <a href="https://erlesolutions.fr" target="_blank" rel="noopener noreferrer" className="inline-block">
            <img src={erleLogo} alt="Erle Solutions" className="h-20 w-auto mx-auto" />
          </a>
          <p className="text-xs text-white/60 mt-3 max-w-[15rem] mx-auto">
            Intégration de solutions digitales et d'IA en entreprise
          </p>
        </div>
      </section>
    </div>
  );
}

export default Home;
